import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { App, Button, Card, Checkbox, Input, Popconfirm, Tag, Tooltip } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, CloseOutlined, DeleteOutlined, LinkOutlined, PlusOutlined, SyncOutlined, } from '@ant-design/icons';
import { deleteBeeyProject, deleteTextReplacement, fetchEventUserlex, updateCreateBeeyProject, updateEventUserlex, upsertTextReplacement, } from 'api/events';
import { useI18n } from 'components/I18nProvider';
import { canStartRecordingForEvent } from 'lib/event-state-merge';
import { beeyProjectUrl } from 'lib/beey-project-url';
import styles from './style.module.scss';
const beeyStateMessageId = (state) => {
    switch (state) {
        case 'Creating':
        case 'Uploading':
            return 'beeyProjectStateUploading';
        case 'Transcribing':
            return 'beeyProjectStateTranscribing';
        case 'Completed':
            return 'beeyProjectStateCompleted';
        case 'Failed':
            return 'beeyProjectStateFailed';
        case 'Canceled':
            return 'beeyProjectStateCanceled';
        default:
            return 'beeyProjectStateUploading';
    }
};
const BeeyProjectStateIcon = ({ state }) => {
    switch (state) {
        case 'Creating':
        case 'Uploading':
            return null;
        case 'Transcribing':
            return _jsx(SyncOutlined, { spin: true });
        case 'Completed':
            return _jsx(CheckCircleOutlined, {});
        case 'Failed':
            return _jsx(CloseCircleOutlined, {});
        case 'Canceled':
            return _jsx(CloseCircleOutlined, {});
        default:
            return null;
    }
};
const TranscriptionPanel = ({ eventId, eventSnapshot, replacements, createBeeyProject, selectedSegmentId, selectedBeeyProjectId, selectedBeeyProjectState, transportOnline, userlexDisabled = false, }) => {
    const { intl } = useI18n();
    const { message } = App.useApp();
    const [expanded, setExpanded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [draft, setDraft] = useState('');
    const [oldTextDraft, setOldTextDraft] = useState('');
    const [newTextDraft, setNewTextDraft] = useState('');
    const [deleteWaiting, setDeleteWaiting] = useState(false);
    useEffect(() => {
        setDeleteWaiting(false);
    }, [selectedSegmentId]);
    const collapse = () => {
        setExpanded(false);
        setDraft('');
    };
    const handleEdit = async () => {
        setLoading(true);
        try {
            const userlex = await fetchEventUserlex(eventId);
            setDraft(userlex ?? '');
            setExpanded(true);
        }
        catch {
            void message.error(intl.formatMessage({ id: 'userlexLoadFailed' }));
        }
        finally {
            setLoading(false);
        }
    };
    const handleSave = async () => {
        setSaving(true);
        try {
            await updateEventUserlex(eventId, draft.trim() || null);
            collapse();
        }
        catch {
            void message.error(intl.formatMessage({ id: 'userlexSaveFailed' }));
        }
        finally {
            setSaving(false);
        }
    };
    const handleAddReplacement = () => {
        const oldText = oldTextDraft;
        const newText = newTextDraft;
        setOldTextDraft('');
        setNewTextDraft('');
        void upsertTextReplacement(eventId, oldText, newText);
    };
    const handleDeleteReplacement = (oldText) => {
        void deleteTextReplacement(eventId, oldText);
    };
    const handleCreateBeeyProjectChange = (checked) => {
        void updateCreateBeeyProject(eventId, checked);
    };
    const handleDeleteBeeyProject = async () => {
        if (selectedSegmentId === null) {
            return;
        }
        setDeleteWaiting(true);
        try {
            await deleteBeeyProject(eventId, selectedSegmentId);
            void message.success(intl.formatMessage({ id: 'beeyProjectDeleted' }));
        }
        catch {
            void message.error(intl.formatMessage({ id: 'beeyProjectDeleteFailed' }));
        }
        finally {
            setDeleteWaiting(false);
        }
    };
    return (_jsxs(Card, { className: styles.panel, children: [_jsxs("div", { className: styles.header, children: [_jsx("span", { className: styles.label, children: intl.formatMessage({ id: 'transcription' }) }), !expanded && (_jsx(Button, { "test-id": "admin-transcription-edit-userlex-button", disabled: userlexDisabled, loading: loading, onClick: () => void handleEdit(), children: intl.formatMessage({ id: 'editUserlex' }) })), expanded && (_jsxs("div", { className: styles.actions, children: [_jsx(Button, { "test-id": "admin-transcription-save-button", type: "primary", loading: saving, disabled: userlexDisabled, onClick: () => void handleSave(), children: intl.formatMessage({ id: 'save' }) }), _jsx(Button, { "test-id": "admin-transcription-cancel-button", disabled: saving || userlexDisabled, onClick: collapse, children: intl.formatMessage({ id: 'cancel' }) })] }))] }), expanded && (_jsx(Input.TextArea, { "test-id": "admin-transcription-userlex-textarea", className: styles.textarea, value: draft, onChange: (e) => setDraft(e.target.value), disabled: userlexDisabled || saving, autoSize: { minRows: 4, maxRows: 20 } })), _jsxs("div", { className: styles.replacementsRow, children: [_jsx("span", { className: styles.replacementsLabel, children: intl.formatMessage({ id: 'textReplacements' }) }), _jsx(Input, { "test-id": "admin-transcription-replacement-old-input", className: styles.replacementInput, placeholder: intl.formatMessage({ id: 'originalWord' }), value: oldTextDraft, onChange: (e) => setOldTextDraft(e.target.value) }), _jsx(Input, { "test-id": "admin-transcription-replacement-new-input", className: styles.replacementInput, placeholder: intl.formatMessage({ id: 'newWord' }), value: newTextDraft, onChange: (e) => setNewTextDraft(e.target.value) }), _jsx(Button, { "test-id": "admin-transcription-add-replacement-button", icon: _jsx(PlusOutlined, {}), onClick: handleAddReplacement, "aria-label": intl.formatMessage({ id: 'addReplacement' }) }), replacements.map((replacement) => (_jsxs(Tag, { closable: true, closeIcon: _jsx(CloseOutlined, { "test-id": "admin-transcription-remove-replacement-button" }), onClose: (e) => {
                            e.preventDefault();
                            handleDeleteReplacement(replacement.oldText);
                        }, className: styles.replacementTag, children: [replacement.oldText, " \u2192 ", replacement.newText] }, replacement.oldText)))] }), _jsxs("div", { className: styles.beeyRow, children: [_jsx(Checkbox, { "test-id": "admin-transcription-create-beey-project-checkbox", checked: createBeeyProject, disabled: !canStartRecordingForEvent(eventSnapshot), onChange: (e) => handleCreateBeeyProjectChange(e.target.checked), children: intl.formatMessage({ id: 'createBeeyProject' }) }), selectedBeeyProjectId != null && selectedSegmentId !== null && (_jsxs("span", { className: styles.beeyProjectActions, children: [_jsxs("a", { "test-id": "admin-transcription-beey-project-link", className: styles.beeyLink, href: beeyProjectUrl(selectedBeeyProjectId), target: "_blank", rel: "noopener noreferrer", children: [_jsx(LinkOutlined, {}), intl.formatMessage({ id: 'beeyProjectLink' })] }), deleteWaiting ? (_jsx("span", { className: styles.beeyDeleteButtonWrap, children: _jsx(Button, { type: "text", size: "small", loading: true, disabled: true, className: styles.beeyDeleteButton, "test-id": "admin-transcription-beey-project-delete-waiting", "aria-label": intl.formatMessage({ id: 'deleteBeeyProject' }) }) })) : (_jsx(Popconfirm, { title: intl.formatMessage({ id: 'deleteBeeyProject' }), description: intl.formatMessage({ id: 'deleteBeeyProjectInfo' }), okText: intl.formatMessage({ id: 'delete' }), cancelText: intl.formatMessage({ id: 'cancel' }), onConfirm: () => void handleDeleteBeeyProject(), okType: "danger", disabled: !transportOnline, okButtonProps: { 'test-id': 'admin-transcription-beey-project-delete-confirm' }, cancelButtonProps: { 'test-id': 'admin-transcription-beey-project-delete-cancel' }, children: _jsx(Tooltip, { title: intl.formatMessage({ id: 'deleteBeeyProject' }), children: _jsx("span", { className: styles.beeyDeleteButtonWrap, children: _jsx(Button, { type: "text", size: "small", icon: _jsx(DeleteOutlined, {}), disabled: !transportOnline, className: styles.beeyDeleteButton, "test-id": "admin-transcription-beey-project-delete-trigger", "aria-label": intl.formatMessage({ id: 'deleteBeeyProject' }) }) }) }) }))] })), selectedBeeyProjectState != null && (_jsxs("span", { className: styles.beeyState, "test-id": "admin-transcription-beey-project-state", children: [_jsx(BeeyProjectStateIcon, { state: selectedBeeyProjectState }), intl.formatMessage({ id: beeyStateMessageId(selectedBeeyProjectState) })] }))] })] }));
};
export default TranscriptionPanel;
